import {
  HttpError,
  IResourceComponentsProps,
  LogicalFilter,
  useMutation,
  useNotification,
} from "@pankod/refine-core";

import {
  ClearOutlined,
  DownloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Row,
  Select,
  Space,
  Table,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import { CustomTag } from "components/extra/custom-tag";
import { DefaultMaskedInput } from "components/extra/default-masked-input";
import { EligibilityTag } from "components/extra/eligibility-tag";
import dayjs from "dayjs";
import { TriggerReportApp, TriggerReportReason } from "enums/triggerReport";
import { ADMIN_BFF_URL } from "environment";
import { ITriggerReports } from "interfaces";
import { useState } from "react";
import { triggerReportAppMap, triggerReportReasonMap } from "utils/constants";
import { exportCSV as _exportCSV } from "utils/exports";
import { formatPhone } from "utils/formaters";
import { useInfiniteSelect } from "hooks/useInfiniteSelect";
import { IPartner } from "interfaces/partner";

interface ITriggerReportsFilter {
  phone: string;
  cpf: string;
  ref_id: string;
  template: string;
  sent_at: string;
  scheduled_datetime: string;
  eligible: string;
  partner: string;
  reason: TriggerReportReason;
  app: TriggerReportApp;
  last_screen: string;
  disparo: number;
}

const normalizeSearchParams = (params: ITriggerReportsFilter) => ({
  phone: params.phone ? params.phone?.replace(/\D/g, "") : null,
  cpf: params.cpf ? params.cpf?.replace(/\D/g, "") : null,
  ref_id: params.ref_id || null,
  template: params.template || null,
  partner: params.partner || null,
  sent_at: params.sent_at ? dayjs(params.sent_at).format("YYYY-MM-DD") : null,
  scheduled_datetime: params.scheduled_datetime
    ? dayjs(params.scheduled_datetime).format("YYYY-MM-DD")
    : null,
  eligible: params.eligible || null,
  reason: params.reason || null,
  app: params.app || null,
  last_screen: params.last_screen || null,
  disparo: params.disparo || null,
});

export const TriggerReportsList: React.FC<IResourceComponentsProps> = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const { open } = useNotification();

  const { tableProps, searchFormProps } = useTable<
    ITriggerReports,
    HttpError,
    ITriggerReportsFilter
  >({
    resource: "v1/bot-integration/prospect/sent/list",
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: enabled,
    onSearch: (values) => {
      const params = normalizeSearchParams(values);
      const filtros: LogicalFilter[] = Object.entries(params).map(
        ([key, value]) => ({
          field: key,
          operator: "eq",
          value,
        })
      );

      const hasFilters = filtros.some((f) => f.value);
      setEnabled(hasFilters);

      return filtros;
    },
    queryOptions: { enabled },
  });

  const { mutate: exportCSV, isLoading: isLoadingExport } = useMutation({
    mutationFn: _exportCSV,
  });

  const handleExportCSV = () => {
    const params =
      searchFormProps.form?.getFieldsValue() as ITriggerReportsFilter;
    exportCSV(
      {
        url: ADMIN_BFF_URL + "/v1/bot-integration/prospect/sent/list/csv",
        filenamePrefix: "rellatorio-de-disparos",
        params: normalizeSearchParams(params),
      },
      {
        onSuccess: () => {
          open?.({
            type: "success",
            message: "Arquivo gerado com sucesso",
          });
        },
        onError: () => {
          open?.({
            type: "error",
            message: "Ocorreu um erro ao gerar o arquivo.",
          });
        },
      }
    );
  };

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    setEnabled(false);
  };

  const { selectProps: selectPropsPartners } = useInfiniteSelect<IPartner>({
        resource: "v1/partners",
        optionLabel: "partner_name",
        optionValue: "partner_code",
        sort: [{ field: "partner_name", order: "asc" }],
        pageSize: 20
   });
  
  const partnerOptions = selectPropsPartners.options?.map((opt) => ({
      ...opt,
      label: `${opt.label}`,
  })) ?? [];

  return (
    <List
      title="Relatórios Disparos"
      headerProps={{
        extra: (
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={
              !tableProps?.dataSource?.length || !enabled || isLoadingExport
            }
            loading={isLoadingExport}
          >
            Exportar CSV
          </Button>
        ),
      }}
    >
      <Card title="Filtrar">
        <Form {...searchFormProps} layout="vertical" style={{ padding: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item
                name="phone"
                label="Telefone"
                rules={[
                  { min: 19, message: "Telefone inválido", required: false },
                ]}
              >
                <DefaultMaskedInput
                  mask="+55 (00) 00000-0000"
                  className="input-custom ant-input"
                  placeholder="Telefone"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item
                name="cpf"
                label="CPF"
                rules={[{ min: 14, message: "CPF inválido", required: false }]}
              >
                <DefaultMaskedInput
                  mask="000.000.000-00"
                  className="input-custom ant-input"
                  placeholder="CPF"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item label="Código do Orçamento" name="ref_id">
                <Input name="ref_id" placeholder="Código" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item name="template" label="Template:">
                <Input placeholder="Template" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item name="partner" label="Parceiro">
                <Select
                  {...selectPropsPartners}
                    options={partnerOptions}
                    placeholder="Parceiro"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item name="sent_at" label="Data/Hora de Envio">
                <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item
                name="scheduled_datetime"
                label="Data/Hora de Previsão"
              >
                <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item label="Elegibilidade" name="eligible">
                <Select placeholder="Elegibilidade">
                  <Select.Option value="all">Ambos</Select.Option>
                  <Select.Option value="1">Sim</Select.Option>
                  <Select.Option value="0">Não</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item label="Motivo" name="reason">
                <Select placeholder="Motivo">
                  {Object.entries(TriggerReportReason).map(([key, value]) => (
                    <Select.Option key={key} value={value}>
                      {triggerReportReasonMap[value].label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item label="App" name="app">
                <Select placeholder="Selecione">
                  {Object.entries(TriggerReportApp).map(([key, value]) => (
                    <Select.Option key={key} value={value}>
                      {triggerReportAppMap[value].label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item label="Ordem de Disparo" name="disparo">
                <InputNumber
                  placeholder="Ordem de Disparo"
                  style={{ width: "100%" }}
                  min={0}
                  type="number"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={6}>
              <Form.Item label="Última Tela" name="last_screen">
                <Input name="last_screen" placeholder="Última Tela" />
              </Form.Item>
            </Col>

            <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
              <Space>
                <Button
                  type="default"
                  icon={<ClearOutlined />}
                  onClick={handleCleanFilters}
                >
                  Limpar
                </Button>

                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                >
                  Buscar
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table
        {...tableProps}
        rowKey="id"
        dataSource={
          enabled && !tableProps.loading ? tableProps?.dataSource : []
        }
        loading={enabled ? tableProps.loading || isLoadingExport : false}
        pagination={{
          ...tableProps.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
      >
        <Table.Column
          dataIndex="phone"
          title="Telefone"
          render={(value) => (
            <TextField
              style={{ whiteSpace: "nowrap" }}
              value={value ? formatPhone(value) : "-"}
            />
          )}
        />

        <Table.Column
          dataIndex="cpf"
          title="CPF"
          render={(value) => (
            <TextField style={{ whiteSpace: "nowrap" }} value={value} />
          )}
        />

        <Table.Column dataIndex="ref_id" title="Código do Orçamento" />

        <Table.Column
          dataIndex="template"
          title="Nome do Template"
          render={(value) => <TextField value={value || "-"} />}
        />

        <Table.Column
          dataIndex="partner"
          key="partner"
          title="Parceiro"
          render={(value) => <TextField value={value || "-"} />}
        />

        <Table.Column
          dataIndex="sent_at"
          title="Data/Hora de Envio"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-"}
            />
          )}
        />

        <Table.Column
          dataIndex="scheduled_datetime"
          title="Data/Hora de Previsão"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-"}
            />
          )}
        />

        <Table.Column dataIndex="disparo" title="Ordem de Disparo" />

        <Table.Column
          dataIndex="app"
          title="App"
          render={(value) => (
            <TextField value={value ? triggerReportAppMap[value].label : "-"} />
          )}
        />

        <Table.Column
          dataIndex="last_screen"
          title="Última tela"
          render={(value) => (
            <TextField style={{ whiteSpace: "nowrap" }} value={value} />
          )}
        />

        <Table.Column
          dataIndex="eligible"
          title="Elegibilidade"
          render={(value: boolean) => <EligibilityTag value={value} />}
        />

        <Table.Column
          dataIndex="motive"
          title="Motivo"
          render={(_, record: ITriggerReports) => {
            if (!record.reason) return "-";
            const { color, label } = triggerReportReasonMap[record.reason];
            return <CustomTag color={color} label={label} />;
          }}
        />
      </Table>
    </List>
  );
};
